import React, { useEffect, useState } from "react";
import { Button, Col, Row, Table, Container, Card } from "react-bootstrap";
import { ShimmerTable } from "react-shimmer-effects";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  DatatableWrapper,
  Filter,
  Pagination,
  PaginationOptions,
  TableBody,
  TableHeader,
} from "react-bs-datatable";
import moment from "moment";
import { FaTrash, FaEdit, FaPlus } from "react-icons/fa";
import TagModal from "./TagModal";

const TagList = () => {
  const [body, setBody] = useState([]);
  const [isSpinner, setIsSpinner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);

  const formatDate = (date) => {
    return moment(date).format("DD MMM YYYY");
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const result = await WhatsAppAPI.getTags();
    if (result.success) {
      setBody(result.records);
    } else {
      setBody([]);
      // toast.error("Failed to fetch tags");
    }
    setIsSpinner(true);
  };

  const handleStatusToggle = async (row) => {
    try {
      const updatedTag = { ...row, status: !row.status };
      const response = await WhatsAppAPI.updateTag(row.id, updatedTag);
      if (response.success) {
        toast.success("Tag status updated successfully");
        fetchData();
      } else {
        toast.error("Failed to update tag status");
      }
    } catch (error) {
      toast.error("Error updating tag status");
    }
  };

  const handleAddNew = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleEdit = (tag) => {
    setEditData(tag);
    setViewOnly(false);
    setShowModal(true);
  };

  const handleView = (tag) => {
    setEditData(tag);
    setViewOnly(true);
    setShowModal(true);
  };

  const handleDelete = async (tagId) => {
    if (window.confirm("Are you sure you want to delete this tag?")) {
      try {
        const response = await WhatsAppAPI.deleteTag(tagId);
        if (response.success) {
          toast.success("Tag deleted successfully");
          fetchData();
        } else {
          toast.error("Failed to delete tag");
        }
      } catch (error) {
        toast.error("Error deleting tag");
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditData(null);
    fetchData();
  };

  const header = [
    {
      title: "Name",
      prop: "name",
      isFilterable: true,
      cell: (row) => (
        <span
          onClick={() => handleView(row)}
          style={{ cursor: "pointer", color: "#007bff" }}
        >
          {row.name}
        </span>
      ),
    },
    {
      title: "Rules",
      prop: "auto_tag_rules",
      cell: (row) => {
        if (
          !row.first_message ||
          !row.auto_tag_rules ||
          row.auto_tag_rules.length === 0
        ) {
          return <span className="text-muted">-</span>;
        }
        return (
          <div style={{ maxWidth: "200px" }}>
            {row.auto_tag_rules.map((rule, index) => (
              <div key={index} className="mb-1">
                <small>
                  <span className="fw-semibold">{rule.keyword}</span>
                  <span className="text-muted"> ({rule.match_type})</span>
                </small>
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: "First Message",
      prop: "first_message",
      isFilterable: true,
      cell: (row) => (
        <span
          className={
            row.first_message === "Yes" ? "text-success" : "text-muted"
          }
        >
          {row.first_message === "Yes" ? "Yes" : "No"}
        </span>
      ),
    },
    {
      title: "Status",
      prop: "status",
      isFilterable: true,
      cell: (row) => (
        <div
          className="form-check form-switch"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            checked={row.status}
            onChange={() => handleStatusToggle(row)}
            style={{ cursor: "pointer" }}
          />
        </div>
      ),
    },
    {
      title: "Created Date",
      prop: "createddate",
      cell: (row) => formatDate(row.createddate),
    },
    {
      title: "Actions",
      prop: "actions",
      cell: (row) => (
        <div className="d-flex gap-2 justify-content-center">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => handleEdit(row)}
          >
            <FaEdit />
          </Button>
        </div>
      ),
    },
  ];

  const labels = {
    beforeSelect: " ",
  };

  return (
    <>
      <Container className="mt-5 mb-5">
        <Row className="mx-5 mb-4">
          <Card className="p-4 bg-white shadow-sm border-0 rounded-3">
            <h5 className="mb-2 fw-bold">💡 Quick Guide</h5>
            <small>
              The first message tag helps identify a user's interest in a
              specific product or service, enabling more personalized follow-ups
            </small>
          </Card>
        </Row>

        <Row className="mx-5 g-0">
          <Col lg={12} className="mb-3">
            <Row className="g-0">
              <Col lg={12}>
                <div className="bg-white shadow-sm border-0 rounded-3 p-4">
                  <h5 className="fw-semibold mb-1">📦 All Tags</h5>

                  {isSpinner ? (
                    <DatatableWrapper
                      body={body}
                      headers={header}
                      paginationOptionsProps={{
                        initialState: {
                          rowsPerPage: 10,
                          options: [5, 10, 15, 20],
                        },
                      }}
                    >
                      <Row className="mb-3">
                        <Col
                          lg={3}
                          sm={10}
                          xs={10}
                          className="d-flex flex-col justify-content-end align-items-end"
                        >
                          <Filter className="p-2" />
                        </Col>
                        <Col
                          lg={4}
                          sm={2}
                          xs={2}
                          className="d-flex flex-col justify-content-start align-items-start"
                        >
                          <PaginationOptions labels={labels} />
                        </Col>
                        <Col
                          lg={5}
                          sm={12}
                          xs={12}
                          className="mt-2 d-flex flex-col justify-content-end align-items-end"
                        >
                          <Button
                            className="btn btn-sm"
                            variant="outline-secondary"
                            onClick={handleAddNew}
                          >
                            <FaPlus /> Add New Tag
                          </Button>
                        </Col>
                      </Row>

                      <div className="table-responsive">
                        <Table
                          striped
                          bordered
                          className="related-list-table"
                          responsive="sm"
                        >
                          <TableHeader />
                          <TableBody />
                        </Table>
                      </div>
                      <Pagination />
                    </DatatableWrapper>
                  ) : (
                    <ShimmerTable row={10} col={4} />
                  )}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>

      <TagModal
        show={showModal}
        onHide={handleModalClose}
        editData={editData}
        viewOnly={viewOnly}
      />

      <ToastContainer />
    </>
  );
};

export default TagList;
